import { useMsal } from "@azure/msal-react";
import { lazy, Suspense, useMemo } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { portal, type Portal } from "./api/client";
import { accessToken, type Settings } from "./auth";
import { ErrorNotice } from "./components/ErrorNotice";
import { Masthead } from "./components/Masthead";
import { Alert, AlertDescription, AlertTitle } from "./components/ui/alert";
import { Skeleton } from "./components/ui/skeleton";
import { TooltipProvider } from "./components/ui/tooltip";
import { SessionContext, useResource } from "./session";
import { TelemetryNavigation } from "./TelemetryNavigation";
const DiscoveryView = lazy(() => import("./views/DiscoveryView").then(module => ({ default: module.DiscoveryView })));
const TeamsView = lazy(() => import("./views/TeamsView").then(module => ({ default: module.TeamsView })));
const LeaseView = lazy(() => import("./views/LeaseView").then((module) => ({ default: module.LeaseView })));
const RolesView = lazy(() => import("./views/RolesView").then((module) => ({ default: module.RolesView })));
const PeopleView = lazy(() => import("./views/PeopleView").then((module) => ({ default: module.PeopleView })));
const PersonView = lazy(() => import("./views/PersonView").then((module) => ({ default: module.PersonView })));
const LifecycleView = lazy(() => import("./views/LifecycleView").then((module) => ({ default: module.LifecycleView })));
const NewRequestView = lazy(() => import("./views/RequestsView").then((module) => ({ default: module.NewRequestView })));
const EditRequestView = lazy(() => import("./views/RequestsView").then((module) => ({ default: module.EditRequestView })));
const RequestsView = lazy(() => import("./views/RequestsView").then((module) => ({ default: module.RequestsView })));
const ReviewsView = lazy(() => import("./views/ReviewsView").then((module) => ({ default: module.ReviewsView })));
const RunsView = lazy(() => import("./views/RunsView").then((module) => ({ default: module.RunsView })));
const RunView = lazy(() => import("./views/RunView").then((module) => ({ default: module.RunView })));

export function ConfigurationNotice() {
  return (
    <main className="mx-auto max-w-[1400px] px-10 py-8">
      <Alert variant="destructive">
        <AlertTitle>The portal is not configured</AlertTitle>
        <AlertDescription>
          Set <code className="font-mono">VITE_TENANT_ID</code> and{" "}
          <code className="font-mono">VITE_PORTAL_CLIENT_ID</code> to the portal app
          registration — never the privileged Graph application.
        </AlertDescription>
      </Alert>
    </main>
  );
}

function Console({ api, onSignOut }: { api: Portal; onSignOut: () => void }) {
  const me = useResource((signal) => api.me(signal), [api]);
  const session = useMemo(
    () => (me.data === null ? null : { api, viewer: me.data }),
    [api, me.data],
  );

  return (
    <>
      <Masthead signedIn viewer={me.data} onSignIn={onSignOut} onSignOut={onSignOut} />
      {me.error && <ErrorNotice error={me.error} />}
      {me.loading && <Skeleton className="h-64 w-full" />}
      {session && (
        <SessionContext value={session}>
          <Suspense fallback={<Skeleton className="h-96 w-full" />}><Routes>
            {/* People is the landing page; preserve older bookmarked links. */}
            <Route path="/this-week" element={<Navigate replace to="/requests?filter=attention" />} />
            <Route path="/roles" element={<RolesView />} />
            <Route path="/people" element={<PeopleView />} />
            <Route path="/teams" element={<TeamsView />} />
            <Route path="/access" element={<DiscoveryView />} />
            <Route path="/people/:upn" element={<PersonView />} />
            <Route path="/requests" element={<RequestsView />} />
            {/* One URL per request from the form onwards, whichever of the
                three it is, so closing the tab and coming back lands wherever
                the work actually got to. */}
            <Route path="/requests/new" element={<NewRequestView />} />
            <Route path="/requests/:requestId/edit" element={<EditRequestView />} />
            <Route path="/requests/:requestId" element={<LifecycleView />} />
            <Route path="/join" element={<Navigate replace to="/requests/new" />} />

            {/* The operator surface, one destination rather than the shape of
                the application. The server still authorizes every read. */}
            <Route path="/it/runs" element={<RunsView />} />
            <Route path="/it/runs/:runId" element={<RunView />} />
            <Route path="/it/reviews" element={<ReviewsView />} />
            <Route path="/it/lease" element={<LeaseView />} />

            {/* Links handed out before the portal grew an HR surface. */}
            <Route path="/runs" element={<Navigate replace to="/it/runs" />} />
            <Route path="/reviews" element={<Navigate replace to="/it/reviews" />} />
            <Route path="/lease" element={<Navigate replace to="/it/lease" />} />

            <Route path="*" element={<Navigate replace to="/people" />} />
          </Routes></Suspense>
        </SessionContext>
      )}
    </>
  );
}

export function App({ config }: { config: Settings }) {
  const { instance, accounts } = useMsal();
  const api = useMemo(
    () => portal(accessToken(instance, config.scope), config.apiBase),
    [instance, config],
  );

  return (
    <BrowserRouter>
      <TelemetryNavigation />
      <TooltipProvider>
        <main className="mx-auto max-w-[1400px] px-4 py-5 pb-12 sm:px-8">
          {accounts.length > 0 ? (
            <Console api={api} onSignOut={() => void instance.logoutRedirect()} />
          ) : (
            <>
              <Masthead
                signedIn={false}
                viewer={null}
                onSignIn={() => void instance.loginRedirect({ scopes: [config.scope] })}
                onSignOut={() => void instance.logoutRedirect()}
              />
              <Alert>
                <AlertTitle>Sign in with your work account</AlertTitle>
                <AlertDescription>
                  This site names people, so every page you open is recorded against your
                  account.
                </AlertDescription>
              </Alert>
            </>
          )}
        </main>
      </TooltipProvider>
    </BrowserRouter>
  );
}
